import React, { useState, useRef } from 'react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { AgGridReact } from 'ag-grid-react';
import * as XLSX from 'xlsx';
import moment from 'moment';
import { toast } from 'react-toastify';
import { ClipLoader } from 'react-spinners';
import { useTranslation } from 'react-i18next';
import LeaseApiService from '../../services/apiService';
import {
    parseExcelDate, parseExcelNumber, toText, dateColumnFormatter,
    amountColumnFormatter, isEnvelopeFailure,
} from './importHelpers';

// Field order/names taken directly from the backend's SH_UnitImportRowVM
// (Pro360ERP API Erini/Pro360.Shared.DomainLayer/ViewModels/
// SH_UnitMasterImportVM.cs), NOT the Excel template's underscored headers -
// same lesson as Property Master Import: that VM's property names are
// camelCase-on-the-wire (propertyNo, unitNo, ...) with NO underscores, and
// only unitSrNo/currentMarketRate are real number types (long?/decimal?) -
// every other field, including date-like completionDate, is a plain string?
// because the underlying tbUnit_Master columns are varchar. Sending a JSON
// number for a string? property throws during model binding -> 400, before
// the action even runs.
const IMPORT_COLUMN_ORDER = [
    'propertyNo', 'propertyName', 'unitNo', 'refNo', 'unitSrNo', 'reraStrNo',
    'streetNo', 'streetName', 'unitOption', 'categoryMix', 'permittedUse', 'unitType',
    'unitCategory', 'unitStyle', 'unitModel', 'unitZone', 'buildingName', 'plotNo',
    'plotSize', 'block', 'floor', 'unitView', 'currentMarketRate', 'completionDate',
    'unitClass', 'livingRooms', 'bedrooms', 'storeRooms', 'parking', 'facilities',
    'status', 'paymentPlan', 'brokerId', 'brokerName', 'sellerId', 'sellerName',
];
// Real number fields on the backend (long?/decimal?) - the only two sent as JSON numbers.
const NUMBER_FIELDS = new Set(['unitSrNo', 'currentMarketRate']);
// completionDate is a string? (varchar column, not a real DateTime) but still
// worth parsing as a date for display/Excel-serial handling - formatted as
// plain 'YYYY-MM-DD' text on save, matching Property Master Import's
// convention for its own string-typed date fields.
const DATE_FIELDS = new Set(['completionDate']);
const WIDE_FIELDS = new Set(['propertyName', 'streetName', 'buildingName', 'facilities']);

const UnitImport = () => {
    const { t } = useTranslation(['financePosting', 'common']);
    const apiService = new LeaseApiService();
    const fileInputRef = useRef();

    const [importRows, setImportRows] = useState([]);
    const [saving, setSaving] = useState(false);

    const columnDefs = IMPORT_COLUMN_ORDER.map((field) => ({
        headerName: t(`financePosting:unitColumns.${field}`),
        field,
        filter: true,
        floatingFilter: true,
        suppressMovable: true,
        width: WIDE_FIELDS.has(field) ? 200 : 130,
        ...(DATE_FIELDS.has(field) ? { valueFormatter: dateColumnFormatter } : {}),
        ...(NUMBER_FIELDS.has(field) ? { valueFormatter: amountColumnFormatter } : {}),
    }));

    const handleImportClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const data = new Uint8Array(evt.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const sheet = workbook.Sheets[workbook.SheetNames[0]];
                const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
                const dataRows = rows.slice(1).filter((row) => row.some((cell) => cell !== '' && cell !== null && cell !== undefined));
                const parsed = dataRows.map((row, index) => {
                    const record = { id: index };
                    IMPORT_COLUMN_ORDER.forEach((field, colIndex) => {
                        const cell = row[colIndex];
                        if (DATE_FIELDS.has(field)) record[field] = parseExcelDate(cell);
                        else if (NUMBER_FIELDS.has(field)) record[field] = parseExcelNumber(cell);
                        else record[field] = toText(cell);
                    });
                    return record;
                });
                setImportRows(parsed);
            } catch (error) {
                console.error('Error parsing Excel file:', error);
                toast.error(t('common:somethingWentWrong', 'Could not read the Excel file'));
            } finally {
                e.target.value = '';
            }
        };
        reader.readAsArrayBuffer(file);
    };

    const handleRefreshImport = () => {
        setImportRows([]);
    };

    const validateImportRows = () => {
        // Matches SH_UnitMasterService.ValidateRows exactly: PropertyNo,
        // UnitNo and RefNo are NOT NULL in tbUnit_Master (RefNo is the PK).
        for (let i = 0; i < importRows.length; i++) {
            const row = importRows[i];
            const rowNo = i + 1;
            if (!row.propertyNo) {
                toast.warning(t('financePosting:validation.propertyNoRequired', { row: rowNo }));
                return false;
            }
            if (!row.unitNo) {
                toast.warning(t('financePosting:validation.unitNoRequired', { row: rowNo }));
                return false;
            }
            if (!row.refNo) {
                toast.warning(t('financePosting:validation.refNoRequired', { row: rowNo }));
                return false;
            }
        }
        return true;
    };

    const handleSave = async () => {
        if (!importRows.length) {
            toast.warning(t('financePosting:noRecordsToSave'));
            return;
        }
        if (!validateImportRows()) return;

        try {
            setSaving(true);
            // Body matches SH_UnitImportSaveVM: { rows: [...] }. completionDate
            // is sent as a plain 'YYYY-MM-DD' string, not a full ISO timestamp -
            // it's a varchar column on the backend.
            const body = {
                rows: importRows.map(({ id, ...rest }) => {
                    const out = { ...rest };
                    DATE_FIELDS.forEach((field) => {
                        out[field] = rest[field] ? moment(rest[field]).format('YYYY-MM-DD') : null;
                    });
                    return out;
                }),
            };
            const res = await apiService.post('UNIT_IMPORT_SAVE', body);
            // This API family wraps responses as { status, data, message } and can
            // return HTTP 200 even for a business-logic failure - axios not
            // throwing isn't enough on its own, check the envelope's status flag too.
            if (isEnvelopeFailure(res)) {
                toast.error(res?.data?.message || t('common:somethingWentWrong', 'Save failed'));
                return;
            }
            toast.success(res?.data?.message || t('financePosting:recordsSavedSuccessfully'));
            setImportRows([]);
        } catch (error) {
            console.error('Error saving unit import rows:', error);
            const serverMessage = error?.response?.data?.message || error?.response?.data?.title;
            toast.error(serverMessage || t('common:somethingWentWrong', 'Save failed'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="p-3">
            <input
                type="file"
                accept=".xlsx,.xls"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileChange}
            />
            <div className="border-box2 p-3" style={{ borderRadius: '8px' }}>
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <div className="d-flex gap-2">
                        <button className="mainbtn" type="button" onClick={handleImportClick}>
                            {t('financePosting:excelImport')}
                        </button>
                        <button className="mainbtnGry" type="button" onClick={handleRefreshImport}>
                            {t('financePosting:refresh')}
                        </button>
                        {saving ? (
                            <button className="mainbtnSecondary" type="button" disabled>
                                <ClipLoader color="#ffffff" size={18} />
                            </button>
                        ) : (
                            <button className="mainbtnSecondary" type="button" onClick={handleSave} disabled={!importRows.length}>
                                {t('financePosting:save')}
                            </button>
                        )}
                    </div>
                    <div className="text-end">
                        <a href="/templates/Unit_Template.xlsx" download="Unit_Template.xlsx" className="d-block mb-1">
                            {t('financePosting:downloadTemplate')}
                        </a>
                        <div>
                            <span className="fw-bold me-1">{t('financePosting:totalRecords')}</span>
                            <span>{importRows.length}</span>
                        </div>
                    </div>
                </div>
                <div className="gridshad mt-3">
                    <div className="ag-theme-quartz" style={{ height: 500 }}>
                        <AgGridReact
                            getRowStyle={(params) => (params.node.rowIndex % 2 === 0 ? { background: '#F0EDF5' } : { background: '#fff' })}
                            rowData={importRows}
                            enableFilter={true}
                            floatingFilter={true}
                            columnDefs={columnDefs}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UnitImport;
